import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Briefcase, Building, ArrowRight, Check, TrendingUp, Factory, Loader2, AlertCircle } from 'lucide-react';

// --- Reusable Components ---

const SectionTag = ({ children }) => (
  <div className="flex items-center gap-3 mb-8">
    <div className="w-12 h-[3px] bg-black"></div>
    <span className="text-xs uppercase tracking-[0.2em] font-bold text-black font-mono">
      {children}
    </span>
  </div>
);

const FeatureItem = ({ title, desc }) => (
  <div className="flex gap-6 items-start mb-8 last:mb-0 group">
    <div className="mt-1 text-black transition-transform group-hover:scale-110 duration-300">
      <div className="w-3 h-3 bg-black rounded-sm"></div>
    </div>
    <div>
      <h4 className="text-base font-bold uppercase tracking-wider mb-2 text-zinc-900">{title}</h4>
      <p className="text-base text-zinc-700 leading-relaxed max-w-md font-serif border-l-2 border-zinc-200 pl-4 py-1">
        {desc}
      </p>
    </div>
  </div>
);

const Partnership = () => {
  const [activeTab, setActiveTab] = useState('brand');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- SEO & Schema Markup ---
  useEffect(() => {
    // 1. Title & Meta Tags
    document.title = "Mitra & Reseller - Peluang Bisnis Konveksi Bandung | iMoonusel";
    
    const setMeta = (name, content) => {
      let element = document.querySelector(`meta[name="${name}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('name', name);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setMeta("description", "Bergabung sebagai mitra bisnis, reseller, atau klien korporat iMoonusel. Dapatkan solusi produksi garmen yang skalabel dan terpercaya.");
    setMeta("keywords", "mitra konveksi, reseller baju, kerjasama konveksi bandung, bisnis garment, produksi makloon");

    // Open Graph
    const setOg = (property, content) => {
      let element = document.querySelector(`meta[property="${property}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute('property', property);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    setOg("og:title", "Mitra & Reseller - Peluang Bisnis Konveksi Bandung | iMoonusel");
    setOg("og:description", "Solusi bisnis untuk brand fashion dan korporat.");
    setOg("og:image", "/logo512.png");
    setOg("og:url", window.location.href);
    setOg("og:type", "website");

    window.scrollTo(0, 0);
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      // Use dynamic hostname to avoid localhost vs 127.0.0.1 issues
      // Add timestamp to prevent caching
      const baseUrl = `${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/get_static_content.php`;
      const res = await axios.get(`${baseUrl}?page=partnership&t=${Date.now()}`);
      
      console.log("Partnership Data Loaded:", res.data);
      if (res.data && Object.keys(res.data).length > 0) {
        setContent(res.data);
      } else {
        setError("API returned empty data.");
      }
    } catch (err) {
      console.error("Failed to fetch partnership content", err);
      setError(err.message || "Failed to load content");
    } finally {
      setLoading(false);
    }
  };

  const getVal = (key) => content?.[key]?.id || '';

  const getPoints = (key) => {
    try {
      return JSON.parse(getVal(key) || '[]');
    } catch (e) {
      return [];
    }
  };

  // Safe Media URL getter
  const getMediaUrl = () => {
      let url = getVal('partnership_hero_media');
      if (!url) return null;
      
      // Clean up URL if it has quotes or whitespace
      url = url.replace(/['"]/g, '').trim();

      // If it's a relative path starting with 'uploads', prepend base URL
      if (url.startsWith('uploads/') || url.match(/^[\w-]+\.(jpg|png|mp4|jpeg)$/)) {
         // Construct Base URL from API URL (remove /api)
         const apiBase = process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api';
         const webBase = apiBase.replace('/api', ''); 
         return `${webBase}/${url}`;
      }
      return url;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="animate-spin text-black" size={48} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-white text-red-600 p-8">
        <AlertCircle size={48} className="mb-4" />
        <h2 className="text-xl font-bold mb-2">Error Loading Page</h2>
        <p className="text-sm font-mono bg-red-50 p-4 rounded">{error}</p>
        <button onClick={() => window.location.reload()} className="mt-6 px-6 py-2 bg-black text-white rounded hover:bg-zinc-800">
            Retry
        </button>
      </div>
    );
  }

  const heroMedia = getMediaUrl();

  return (
    <div className="min-h-screen bg-white text-zinc-900 font-sans pt-20">
      
      {/* Header Section */}
      <section className="pt-20 pb-24 border-b border-black">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-end">
          <div>
            <SectionTag>Partnership</SectionTag>
            <h1 className="text-6xl md:text-8xl font-black tracking-tighter leading-[0.85] mb-8 text-black">
              {getVal('partnership_hero_title') || 'Partnership'}
            </h1>
          </div>
          <div className="lg:pl-12 lg:pb-4">
             <p className="text-xl md:text-2xl text-zinc-800 font-serif leading-relaxed">
              {getVal('partnership_hero_desc') || <span className="text-red-500 italic">No description found in database.</span>}
            </p>
          </div>
        </div>
      </section>

      {/* Hero Media (Optional) */}
      {heroMedia && (
        <section className="w-full h-[60vh] overflow-hidden border-b border-black relative">
            {heroMedia.match(/\.(mp4|webm)$/i) ? (
                 <video 
                 src={heroMedia} 
                 className="w-full h-full object-cover"
                 autoPlay loop muted playsInline
                 onError={(e) => console.error("Video load error:", e)}
               />
            ) : (
                <img 
                src={heroMedia} 
                alt="Partnership Hero" 
                className="w-full h-full object-cover"
                onError={(e) => {
                    console.error("Image load error:", e.target.src);
                    e.target.style.display = 'none';
                }}
              />
            )}
        </section>
      )}

      {/* Tab Navigation */}
      <section className="sticky top-20 z-40 bg-white/95 backdrop-blur-md border-b border-black">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex">
            <button 
              onClick={() => setActiveTab('brand')}
              className={`py-6 px-8 text-xs font-bold uppercase tracking-[0.2em] transition-all border-r border-l border-zinc-200 ${
                activeTab === 'brand' 
                  ? 'bg-black text-white' 
                  : 'bg-white text-zinc-500 hover:text-black hover:bg-zinc-50'
              }`}
            >
              For Brands & Corporate
            </button>
            <button 
              onClick={() => setActiveTab('investor')}
              className={`py-6 px-8 text-xs font-bold uppercase tracking-[0.2em] transition-all border-r border-zinc-200 ${
                activeTab === 'investor' 
                  ? 'bg-black text-white' 
                  : 'bg-white text-zinc-500 hover:text-black hover:bg-zinc-50'
              }`}
            >
              For Investors
            </button>
          </div>
        </div>
      </section>

      {/* Content Area */}
      <section className="py-24 bg-zinc-50 min-h-[60vh]">
        <div className="max-w-7xl mx-auto px-6">
          
          {/* BRAND CONTENT */}
          {activeTab === 'brand' && (
            <div className="grid md:grid-cols-12 gap-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
              <div className="md:col-span-7">
                <h2 className="text-5xl font-black tracking-tighter uppercase mb-8 leading-[0.9]">
                    {getVal('partnership_brand_title') || 'Solusi Produksi Skalabel.'}
                </h2>
                <p className="text-xl text-zinc-800 mb-16 font-serif leading-loose max-w-2xl border-b border-zinc-300 pb-12">
                   {getVal('partnership_brand_desc')}
                </p>
                
                <div>
                   <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-10">Why Partner With Us?</h3>
                   <div className="space-y-4">
                       {getPoints('partnership_brand_points').map((pt, idx) => (
                           <FeatureItem key={idx} title={pt.title} desc={pt.desc} />
                       ))}
                       {getPoints('partnership_brand_points').length === 0 && (
                           <p className="text-zinc-400 italic">Belum ada poin data.</p>
                       )}
                   </div>
                </div>
              </div>

              <div className="md:col-span-5">
                <div className="bg-white p-12 border-2 border-black h-full flex flex-col justify-between shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                    <div>
                        <Factory size={56} strokeWidth={1.5} className="text-black mb-10" />
                        <h3 className="text-3xl font-bold uppercase tracking-tight mb-6">
                            {getVal('partnership_brand_cta_title') || 'Mulai Kontrak Produksi.'}
                        </h3>
                        <p className="text-zinc-600 mb-10 leading-relaxed text-lg">
                            {getVal('partnership_brand_cta_desc') || 'Siap untuk menstabilkan rantai pasokan Anda? Mari diskusikan kebutuhan kapasitas bulanan Anda.'}
                        </p>
                    </div>
                    <a 
                    href="/contact" 
                    className="flex items-center justify-between w-full py-5 px-8 bg-black text-white hover:bg-zinc-800 transition-all group"
                    >
                    <span className="text-sm font-bold uppercase tracking-widest">Contact Sales Team</span>
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>
              </div>
            </div>
          )}

          {/* INVESTOR CONTENT */}
          {activeTab === 'investor' && (
            <div className="grid md:grid-cols-12 gap-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
               <div className="md:col-span-7">
                <h2 className="text-5xl font-black tracking-tighter uppercase mb-8 leading-[0.9]">
                    {getVal('partnership_investor_title') || 'Pertumbuhan Sektor Riil.'}
                </h2>
                <p className="text-xl text-zinc-800 mb-16 font-serif leading-loose max-w-2xl border-b border-zinc-300 pb-12">
                   {getVal('partnership_investor_desc')}
                </p>
                
                <div>
                   <h3 className="text-xs font-bold uppercase tracking-widest text-zinc-400 mb-10">Investment Philosophy</h3>
                   <div className="space-y-4">
                        {getPoints('partnership_investor_points').map((pt, idx) => (
                           <FeatureItem key={idx} title={pt.title} desc={pt.desc} />
                       ))}
                       {getPoints('partnership_investor_points').length === 0 && (
                           <p className="text-zinc-400 italic">Belum ada poin data.</p>
                       )}
                   </div>
                </div>
              </div>

              <div className="md:col-span-5">
                <div className="bg-white p-12 border-2 border-black h-full flex flex-col justify-between shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
                    <div>
                        <TrendingUp size={56} strokeWidth={1.5} className="text-black mb-10" />
                        <h3 className="text-3xl font-bold uppercase tracking-tight mb-6">
                            {getVal('partnership_investor_cta_title') || 'Alokasi Privat.'}
                        </h3>
                        <p className="text-zinc-600 mb-10 leading-relaxed text-lg">
                            {getVal('partnership_investor_cta_desc') || 'Diskusikan potensi partisipasi ekuitas atau struktur pembiayaan hutang dengan departemen keuangan kami.'}
                        </p>
                    </div>
                    <a 
                    href="/contact" 
                    className="flex items-center justify-between w-full py-5 px-8 bg-black text-white hover:bg-zinc-800 transition-all group"
                    >
                    <span className="text-sm font-bold uppercase tracking-widest">Schedule Meeting</span>
                    <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                    </a>
                </div>
              </div>
            </div>
          )}

        </div>
      </section>
    </div>
  );
};

export default Partnership;
